import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { CatalogFilterValue } from '../../../redux/api/fymAPI.types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CatalogFilterChipsProps {
  value: CatalogFilterValue;
  onChange: (value: CatalogFilterValue) => void;
}

interface ChipProps {
  value: CatalogFilterValue;
  label: string;
  active: boolean;
  onSelect: (value: CatalogFilterValue) => void;
}

const ChipComponent: React.FC<ChipProps> = ({ value, label, active, onSelect }) => {
  const select = useCallback(() => onSelect(value), [onSelect, value]);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={clsx(styles.chip, active && styles.chipOn)}
      onClick={select}
    >
      {label}
    </button>
  );
};

const Chip = TypedMemo(ChipComponent);

/**
 * The four slices the server can answer for.
 *
 * The design also drew a **Verified** chip. It is left out until something can
 * verify a release — a filter that always returns nothing teaches the artist
 * that the screen is broken.
 */
const CatalogFilterChipsComponent: React.FC<CatalogFilterChipsProps> = ({ value, onChange }) => {
  const intl = useIntl();

  const chips = useMemo(
    () => [
      {
        value: CatalogFilterValue.all,
        label: intl.formatMessage({ id: 'app.sml.catalog.filter.all', defaultMessage: 'All' }),
      },
      {
        value: CatalogFilterValue.claimed,
        label: intl.formatMessage({ id: 'app.sml.catalog.filter.claimed', defaultMessage: 'Claimed' }),
      },
      {
        value: CatalogFilterValue.unreleased,
        label: intl.formatMessage({ id: 'app.sml.catalog.filter.unreleased', defaultMessage: 'Unreleased' }),
      },
      {
        value: CatalogFilterValue.noLink,
        label: intl.formatMessage({ id: 'app.sml.catalog.filter.no_link', defaultMessage: 'No smartlink' }),
      },
    ],
    [intl]
  );

  return (
    <div
      className={styles.chips}
      role="radiogroup"
      aria-label={intl.formatMessage({ id: 'app.sml.catalog.filter.label', defaultMessage: 'Filter the catalog' })}
    >
      {chips.map((chip) => (
        <Chip
          key={chip.value}
          value={chip.value}
          label={chip.label}
          active={chip.value === value}
          onSelect={onChange}
        />
      ))}
    </div>
  );
};

export const CatalogFilterChips = TypedMemo(CatalogFilterChipsComponent);
